import { formatContactDisplayName, contactTileEmailLine, contactIsDeceased, type Contact } from "@domaddress/shared";
import DeceasedMark from "./DeceasedMark";
import GroupActivityBadges from "./GroupActivityBadges";
import ContactGroupBadges from "./ContactGroupBadges";

interface Props {
  contact: Contact;
  /** Ontbreekt bij alleen-lezen rollen: geen bewerk/verwijder-knoppen. */
  onEdit?: () => void;
  onDelete?: () => void;
  /** Klik op de kaart (niet bewerken/verwijderen) opent meestal detailweergave */
  onView?: () => void;
  onAddToSendList?: () => void;
  /** Optioneel: label van de primaire adresboek-lijst (bijv. op “alle contacten”) */
  primaryListLabel?: string;
}

const API_URL = import.meta.env.VITE_API_URL ?? "http://localhost:3000";

export default function ContactCard({ contact, onEdit, onDelete, onView, onAddToSendList, primaryListLabel }: Props) {
  const email = contactTileEmailLine(contact);
  const initials = `${contact.first_name[0] ?? ""}${contact.last_name[0] ?? ""}`.toUpperCase();
  const deceased = contactIsDeceased(contact);

  return (
    <div
      className={`contact-card${onView ? " contact-card--clickable" : ""}${deceased ? " contact-card--deceased" : ""}`}
      onClick={onView}
      onKeyDown={
        onView
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onView();
              }
            }
          : undefined
      }
      role={onView ? "button" : undefined}
      tabIndex={onView ? 0 : undefined}
    >
      <div className="contact-card-header">
        <div className="contact-avatar" aria-hidden>
          {contact.profile_picture
            ? <img src={`${API_URL}${contact.profile_picture}`} alt={initials} />
            : <span>{initials}</span>
          }
        </div>
        <div className="contact-actions" onClick={(e) => e.stopPropagation()}>
          {onAddToSendList && (
            <button
              type="button"
              className="btn-primary btn-sm"
              onClick={onAddToSendList}
              title="Toevoegen aan deze e-mail"
              style={{
                fontWeight: 700,
                minWidth: "7.5rem",
                boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
              }}
            >
              Toevoegen →
            </button>
          )}
          {onEdit && (
            <button className="btn-icon" onClick={onEdit} title="Bewerken" type="button">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                <path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4Z"/>
              </svg>
            </button>
          )}
          {onDelete && (
            <button className="btn-icon btn-icon-danger" onClick={onDelete} title="Verwijderen" type="button">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="3,6 5,6 21,6"/><path d="M19,6l-1,14a2,2,0,0,1-2,2H8a2,2,0,0,1-2-2L5,6"/>
                <path d="M10,11v6"/><path d="M14,11v6"/><path d="M9,6V4h6v2"/>
              </svg>
            </button>
          )}
        </div>
      </div>
      <div className="contact-info">
        <h3 className="contact-name" style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          {formatContactDisplayName(contact)}
          {deceased ? <DeceasedMark /> : null}
        </h3>
        {email && (
          <a href={`mailto:${email}`} className="contact-detail" onClick={(e) => e.stopPropagation()}>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22,7-10,7L2,7"/>
            </svg>
            {email}
          </a>
        )}
        {contact.phone && (
          <a href={`tel:${contact.phone}`} className="contact-detail" onClick={(e) => e.stopPropagation()} title="Telefoon vast">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.56 1.12h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L7.91 8.56a16 16 0 0 0 6 6l1.55-1.55a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
            </svg>
            {contact.phone} <span className="data-list-row-muted" style={{ fontSize: "0.85em" }}>(vast)</span>
          </a>
        )}
        {contact.phone_mobile && (
          <a href={`tel:${contact.phone_mobile}`} className="contact-detail" onClick={(e) => e.stopPropagation()} title="Telefoon mobiel">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.56 1.12h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L7.91 8.56a16 16 0 0 0 6 6l1.55-1.55a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
            </svg>
            {contact.phone_mobile} <span className="data-list-row-muted" style={{ fontSize: "0.85em" }}>(mobiel)</span>
          </a>
        )}
        {(contact.city || contact.country) && (
          <span className="contact-detail contact-location">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M20 10c0 6-8 12-8 12S4 16 4 10a8 8 0 0 1 16 0Z"/><circle cx="12" cy="10" r="3"/>
            </svg>
            {[contact.city, contact.country].filter(Boolean).join(", ")}
          </span>
        )}
        {(primaryListLabel ||
          contact.bezoekersbestand_id != null ||
          (contact.groups && contact.groups.length > 0) ||
          contact.group_activity) && (
          <div className="contact-groups">
            {primaryListLabel && (
              <span className="group-badge" title="Primaire adresboek-lijst">
                {primaryListLabel}
              </span>
            )}
            <GroupActivityBadges contact={contact} />
            {contact.bezoekersbestand_id != null && (
              <span className="group-badge" title="Komt uit het bezoekersbestand">
                Bezoeker #{contact.bezoekersbestand_id}
              </span>
            )}
            <ContactGroupBadges contact={contact} />
          </div>
        )}
      </div>
    </div>
  );
}
